import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Send, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChat = () => {
  const [sessions, setSessions] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(() => {
      fetchSessions();
      fetchUnreadCount();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedSession, sessions]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/admin/sessions`);
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/admin/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedSession) return;

    try {
      await axios.post(`${BACKEND_URL}/api/chat/admin/reply`, {
        session_id: selectedSession,
        message: replyMessage
      });
      setReplyMessage('');
      fetchSessions();
    } catch (error) {
      console.error('Error sending reply:', error);
    }
  };

  const markAsRead = async (sessionId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/chat/admin/mark-read/${sessionId}`);
      setSelectedSession(sessionId);
      fetchSessions();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const sessionsList = Object.entries(sessions).sort((a, b) => {
    const aLastMsg = a[1].last_message?.timestamp || a[1].session.started_at;
    const bLastMsg = b[1].last_message?.timestamp || b[1].session.started_at;
    return new Date(bLastMsg) - new Date(aLastMsg);
  });

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white">Admin Chat</h1>
            <Badge className="bg-purple-600 text-white text-lg px-4 py-2">
              {unreadCount} Unread
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
            {/* Sessions List */}
            <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10 overflow-hidden">
              <CardContent className="p-0 h-full overflow-y-auto">
                <div className="p-4 border-b border-white/10 bg-purple-600/20">
                  <h3 className="font-bold text-white">Conversations</h3>
                </div>
                {sessionsList.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <div>
                    {sessionsList.map(([sessionId, session]) => (
                      <div
                        key={sessionId}
                        onClick={() => markAsRead(sessionId)}
                        className={`p-4 border-b border-white/10 cursor-pointer transition-colors ${
                          selectedSession === sessionId
                            ? 'bg-purple-600/20'
                            : 'hover:bg-white/5'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                              <User size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {session.messages[0]?.user_name || 'Guest'}
                              </p>
                              <p className="text-xs text-gray-400">
                                {session.messages[0]?.user_email || 'No email'}
                              </p>
                            </div>
                          </div>
                          {session.unread_count > 0 && (
                            <Badge className="bg-red-600 text-white">
                              {session.unread_count}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 truncate">
                          {session.last_message?.message || 'No messages'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat Messages */}
            <Card className="lg:col-span-2 bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10 overflow-hidden flex flex-col">
              {selectedSession ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-white/10 bg-purple-600/20">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                        <User size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold">
                          {sessions[selectedSession]?.messages[0]?.user_name || 'Guest'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {sessions[selectedSession]?.messages[0]?.user_email || 'No email'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {sessions[selectedSession]?.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.sender === 'admin'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-800 text-white'
                          }`}
                        >
                          {msg.sender === 'user' && (
                            <p className="text-xs text-purple-400 mb-1">
                              {msg.user_name || 'Guest'}
                            </p>
                          )}
                          <p className="text-sm break-words">{msg.message}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(msg.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Input */}
                  <div className="p-4 border-t border-white/10">
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Type your reply..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="bg-black/50 border-white/10 text-white"
                      />
                      <Button onClick={sendReply} className="bg-purple-600 hover:bg-purple-700">
                        <Send size={18} />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <MessageCircle size={64} className="mx-auto mb-4 opacity-50" />
                    <p>Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminChat;
